import { createDefaultLogger } from "@/types/console-logger";
const logger = createDefaultLogger();

import {
    buildStandardHeaders,
    defaultReasonForAbortingApiCall,
    defaultTimeoutInMilliseconds,
    goFetch
} from "@/utils/network-utils";
import type { ApplicationIncidentPayload } from "@tmi/types/api/fetch";
import type { DataSyncEvent } from "@/utils/data-sync-event-factory";
import type { UserProfileDataApiResponse } from "@tmi/types/api/user";
import { Forbidden, InternalError, NotFound, Unauthorized } from "@tmi/types/api/responses";
import { MessageTypes } from "@tmi/types/enums/message-types";
import { ConfigurationError, Unknown } from "@tmi/types/errors";
// constants.ts imports nothing, so the sync worker bundle stays free of Vue
import { NewPasswordName, SignInPath } from "@/constants";

export function notifyAppIncident( name: string, msg: object, safeCtx: object ): void {
    goFetch< void, ApplicationIncidentPayload >( {
        endpoint: `ux/user/appincident`,
        payload: {
            relatedActionName: name,
            message: JSON.stringify( msg ),
            safeActionContext: safeCtx
        }
    } ).then( () => {
        /* NO-OP */
    } ).catch( ( err: any ) => logger.warn( JSON.stringify( err.message ) ) );
}

interface IStateService {
    handleClientError(): void;
    queueClientError( err: any, ctx: any ): void;
    isFaulted(): boolean;
    onUnauthorized( handler: () => void ): () => void;
}

type ApplicationError = {
    stackTrace: string | undefined,
    ctx: any,
    name?: string,
    message?: string,
    status?: number,
    response?: string
}

// UX roadmap #17: how much the client error monitor may send. Per JavaScript context (the window and the sync worker
// each keep their own), and per page load. A render error in a list row, or a failing request on a retry loop, repeats
// itself many times a minute, and before these limits every repeat was a POST from the member's browser.

/** A report identical to one already sent within this window is counted, not sent; the next copy carries the count. */
export const MonitorDedupeWindowMilliseconds = 10 * 60_000;
/** At most this many reports a minute. Reports over the cap are counted and the count rides on the next one sent. */
export const MonitorReportsPerMinute = 10;
/** At most this many reports per page load, whatever the rate. */
export const MonitorReportsPerSession = 100;

const MonitorMessageMaxLength = 1_000;
const MonitorStackMaxLength = 4_000;
const MonitorContextMaxLength = 2_000;

/** What the monitor POSTs: the queued error plus where, which build and how many copies were held back. */
export type ClientErrorReport = ApplicationError & {
    occurredAt: string,
    /** The page's path (no query or hash, and credentials in the path redacted), or 'worker' for the sync worker. */
    path: string,
    release: string | undefined,
    /** Copies of this same report held back by the dedupe window since it was last sent. */
    repeats: number,
    /** Other reports dropped by the rate or session cap since the last one sent. */
    dropped: number
};

/**
 * Context properties whose value must never leave the page. `payload` is a request body, which goFetch queues with
 * every confirmed 401: the change-password form sends the current and new passwords, the change-email form an address.
 */
const CredentialPropertyNames: ReadonlySet< string > = new Set( [ 'payload', 'challengeid', 'authorization', 'email' ] );
/** Any property, or `{ key, value }` pair key (tenantContext), that names a password, token or secret. */
const CredentialNamePattern = /pass|token|answer|secret/i;

function isCredentialName( name: string ): boolean {
    return CredentialPropertyNames.has( name.toLowerCase() ) || CredentialNamePattern.test( name );
}

/** Paths whose parameters are credentials: the sign-in-as token, the password-reset challenge and answer. */
const CredentialPathPrefixes: readonly string[] = [ `${ SignInPath }/`, `/${ NewPasswordName }/` ];

/**
 * `path` with everything after a credential route's prefix replaced: '/sign-in/<token>' becomes '/sign-in/[redacted]',
 * '/new-password/<challenge>/<answer>' becomes '/new-password/[redacted]'. Any other path is returned as it is.
 * For every route path a report carries, not only the page's own: a failed navigation reports where it was going.
 */
export function redactCredentialPath( path: string ): string {
    const prefix = CredentialPathPrefixes.find( ( credentialPrefix: string ) => path.startsWith( credentialPrefix ) );
    return prefix ? `${ prefix }[redacted]` : path;
}

/**
 * Never throws: cycles become '[Circular]', an Error its name and message, a bigint its digits.
 * With `redactCredentials`, a credential-named property, or the value of a credential-keyed `{ key, value }` pair, is
 * '[redacted]', and a string that is a credential route's path goes through redactCredentialPath whatever its key.
 */
function safeStringify( value: unknown, redactCredentials = false ): string {
    const seen = new WeakSet< object >();
    try {
        return JSON.stringify( value, function ( this: unknown, key: string, item: unknown ) {
            if ( redactCredentials && key && item !== undefined ) {
                const pairKey = ( this as { key?: unknown } | null )?.key;
                const isCredentialPairValue = key === 'value' && typeof pairKey === 'string'
                    && CredentialNamePattern.test( pairKey );
                if ( isCredentialPairValue || isCredentialName( key ) ) {
                    return '[redacted]';
                }
            }
            if ( redactCredentials && typeof item === 'string' ) {
                // Names cannot catch a route path under an innocent key (a failed navigation's `to`)
                return redactCredentialPath( item );
            }
            if ( typeof item === 'bigint' ) {
                return item.toString();
            }
            if ( item instanceof Error ) {
                return { name: item.name, message: item.message };
            }
            if ( item && typeof item === 'object' ) {
                if ( seen.has( item ) ) {
                    return '[Circular]';
                }
                seen.add( item );
            }
            return item;
        } ) ?? String( value );
    } catch {
        return String( value );
    }
}

function errorMessage( err: unknown ): string {
    if ( typeof err === 'string' ) {
        return err;
    }
    const message = ( err as { message?: unknown } | null | undefined )?.message;
    return typeof message === 'string' ? message : safeStringify( err );
}

function truncate( text: string | undefined, maxLength: number ): string | undefined {
    return text && text.length > maxLength ? `${ text.slice( 0, maxLength ) }…` : text;
}

function reportPath(): string {
    if ( typeof window === 'undefined' ) {
        return 'worker';
    }
    return redactCredentialPath( window.location.pathname );
}

function clientMonitorUri(): string {
    return `${ import.meta.env.VITE_CLIENT_MONITOR_URI ?? '' }`.trim();
}

class ClientErrorMonitor {
    /** Last send and held-back copies, by report fingerprint. Bounded by MonitorReportsPerSession. */
    readonly #sent = new Map< string, { at: number, repeats: number } >();

    #minuteStartedAt = 0;
    #sentThisMinute = 0;
    #sentThisSession = 0;
    #dropped = 0;

    /**
     * Sends each error that passes the dedupe window and the caps, one request each, without waiting on any of them.
     * Nothing is sent, or counted, while VITE_CLIENT_MONITOR_URI is unset (ops supplies the endpoint).
     */
    public report( errors: readonly ApplicationError[] ): void {
        const monitorUri = clientMonitorUri();
        if ( !monitorUri ) {
            return;
        }

        for ( const appError of errors ) {
            const report = this.admit( appError, Date.now() );
            if ( report ) {
                this.send( monitorUri, report );
            }
        }
    }

    public reset(): void {
        this.#sent.clear();
        this.#minuteStartedAt = 0;
        this.#sentThisMinute = 0;
        this.#sentThisSession = 0;
        this.#dropped = 0;
    }

    private admit( appError: ApplicationError, now: number ): ClientErrorReport | null {
        const message = truncate( appError.message, MonitorMessageMaxLength );
        // Redacted before anything else reads it: the monitor need not be ours (UX roadmap #17 review)
        const ctxJson = truncate( safeStringify( appError.ctx ?? {}, true ), MonitorContextMaxLength ) ?? '{}';
        const fingerprint = `${ appError.status }|${ appError.name ?? '' }|${ message ?? '' }|${ ctxJson }`;

        const previous = this.#sent.get( fingerprint );
        if ( previous && now - previous.at < MonitorDedupeWindowMilliseconds ) {
            previous.repeats++;
            return null;
        }

        if ( now - this.#minuteStartedAt >= 60_000 ) {
            this.#minuteStartedAt = now;
            this.#sentThisMinute = 0;
        }
        if ( this.#sentThisSession >= MonitorReportsPerSession || this.#sentThisMinute >= MonitorReportsPerMinute ) {
            this.#dropped++;
            return null;
        }

        this.#sentThisMinute++;
        this.#sentThisSession++;
        this.#sent.set( fingerprint, { at: now, repeats: 0 } );
        const dropped = this.#dropped;
        this.#dropped = 0;

        let ctx: unknown;
        try {
            ctx = JSON.parse( ctxJson );
        } catch {
            // Cut off by the length cap: send what fits as text
            ctx = ctxJson;
        }

        return {
            status: appError.status,
            name: appError.name,
            message,
            stackTrace: truncate( appError.stackTrace, MonitorStackMaxLength ),
            ctx,
            occurredAt: new Date( now ).toISOString(),
            path: reportPath(),
            release: typeof __APP_VERSION__ === 'undefined' ? undefined : __APP_VERSION__,
            repeats: previous?.repeats ?? 0,
            dropped
        };
    }

    private send( monitorUri: string, report: ClientErrorReport ): void {
        // Short-circuit a call that is taking too long
        const monitor = new AbortController();
        const timeoutId = setTimeout( () => monitor.abort( defaultReasonForAbortingApiCall ), defaultTimeoutInMilliseconds );

        // No token: the monitor need not be ours, and a report must not carry the member's credentials.
        // keepalive: a member who meets an error often reloads at once, which would cancel a plain request.
        fetch( monitorUri, {
            method: 'POST',
            body: safeStringify( report ),
            headers: buildStandardHeaders(),
            keepalive: true,
            signal: monitor.signal
        } ).catch( ( err: unknown ) => {
            // Never queued: a report about the reporter would loop
            logger.error( 'Client error report failed', err );
        } ).finally( () => {
            clearTimeout( timeoutId );
        } );
    }
}

const clientErrorMonitor = new ClientErrorMonitor();

/** Clears the monitor's dedupe window and caps. For specs. */
export function resetClientErrorMonitor(): void {
    clientErrorMonitor.reset();
}

enum StateMachine {
    Uninitialized = 0,
    Unauthenticated = 1,
    Faulted = 3
}

class ClientStateService implements IStateService {
    readonly #errors = new Array< ApplicationError >();

    #state: StateMachine = StateMachine.Uninitialized;

    #unauthorizedHandler: ( () => void ) | null = null;

    constructor() {
        this.clearState();
    }

    private clearState(): void {
        // Remove all elements currently in the errors collection
        this.#errors.splice( 0 );
        // Set the FSM to the baseline state "unauthenticated"
        this.#state = StateMachine.Unauthenticated;
    }

    public isFaulted(): boolean {
        return this.#state === StateMachine.Faulted;
    }

    /**
     * UX roadmap #16: who ends the session when a 401 is confirmed on the main thread (goFetch, a Pinia Colada query).
     * Registered once, by the Application store; the returned function unregisters it.
     */
    public onUnauthorized( handler: () => void ): () => void {
        this.#unauthorizedHandler = handler;
        return () => {
            if ( this.#unauthorizedHandler === handler ) {
                this.#unauthorizedHandler = null;
            }
        };
    }

    private _postMessage( message: DataSyncEvent ) {
        if ( typeof window === 'undefined' ) {
            // The sync worker: the main thread's worker.onmessage routes it (an UNAUTHORIZED ends the session there).
            postMessage( message );
            return;
        }

        // UX roadmap #16: this used to window.postMessage() every kind, but the only window listener accepted
        // 'tmi_sync_evt', so a 401 on the main thread never signed the member out, and any frame could have posted a
        // forged one. The other kinds were only ever logged, and the logger is a no-op in production.
        const handler = this.#unauthorizedHandler;
        if ( MessageTypes.UNAUTHORIZED === message.eventType && handler ) {
            // After the caller's own rejection handling, not inside goFetch's catch.
            queueMicrotask( handler );
        }
    }

    public handleClientError() {
        // Taken before anything else runs, so an error queued while these are handled starts its own batch
        const errors = this.#errors.splice( 0 );

        errors.forEach( ( err: ApplicationError ) => {
            if ( Unauthorized.statusCode === err.status ) {
                this._postMessage( {
                    typeSignifier: 'tmi_error',
                    eventType: MessageTypes.UNAUTHORIZED
                } );
            } else if ( Forbidden.statusCode === err.status ) {
                this._postMessage( {
                    typeSignifier: 'tmi_error',
                    eventType: MessageTypes.FORBIDDEN
                } );
            } else if ( InternalError.statusCode === err.status ) {
                this._postMessage( {
                    typeSignifier: 'tmi_error',
                    eventType: MessageTypes.INTERNAL_ERROR
                } );
            } else if ( NotFound.statusCode === err.status ) {
                this._postMessage( {
                    typeSignifier: 'tmi_error',
                    eventType: MessageTypes.NOT_FOUND
                } );
            }  else if ( ConfigurationError.statusCode === err.status ) {
                this._postMessage( {
                    typeSignifier: 'tmi_error',
                    eventType: MessageTypes.CONFIGURATION,
                    payload: err?.message
                } );
            } else if ( Unknown.statusCode === err.status ) {
                logger.warn( err.stackTrace! );

                this._postMessage( {
                    typeSignifier: 'tmi_error',
                    eventType: MessageTypes.UNKNOWN,
                    payload: err.stackTrace
                } );
            }
        } );

        // UX roadmap #17: once per batch, not once per error. This ran inside the loop above and itself sent every
        // queued error, so a batch of n errors went out n² times; the monitor now also drops repeats and caps volume.
        clientErrorMonitor.report( errors );

        this.clearState();
    }

    public queueClientError( err: any, ctx: any ) {
        // Anything can be thrown, null and undefined included: reading `.message` off those threw from the reporter
        const message = errorMessage( err );
        let status = Unknown.statusCode;
        let isCriticalError = false;

        if ( err instanceof Unauthorized ) {
            status = Unauthorized.statusCode;
        } else if ( err instanceof Forbidden ) {
            status = Forbidden.statusCode;
            isCriticalError = true; // Permission errors should fault the client - indicates access violation
        } else if ( err instanceof InternalError ) {
            status = InternalError.statusCode;
            // Internal errors logged but don't fault - could be transient
        } else if ( err instanceof NotFound ) {
            status = NotFound.statusCode;
            // Not found errors logged but don't fault - could be expected
        } else if ( err instanceof ConfigurationError ) {
            status = ConfigurationError.statusCode;
            isCriticalError = true; // Config errors should fault the client
        } else {
            // safeStringify: the argument is built even though the logger is a no-op in production, and a circular
            // value made JSON.stringify throw out of the reporter
            logger.warn( safeStringify( err ) );
        }

        this.#errors.push( {
            // The error's own stack says where it was thrown; this call's own says only who reported it
            stackTrace: ( typeof err?.stack === 'string' && err.stack ) ? err.stack : new Error().stack,
            name: typeof err?.name === 'string' ? err.name : undefined,
            ctx: ctx,
            status: status,
            message: message
        } );

        // Only set state to Faulted for critical errors (permissions, config)
        // Unauthorized does NOT fault - allows user to re-authenticate
        // Other errors are logged but don't block all API calls
        if ( isCriticalError ) {
            this.#state = StateMachine.Faulted;
        }

        this.handleClientError();
    }
}

export const clientStateManager: IStateService = new ClientStateService();

/** Failure streaks already reported, by streak key. Per JavaScript context: the window and the sync worker each keep their own. */
const reportedFailureStreaks = new Set< string >();

/**
 * UX roadmap #58: queues `err` once per failure streak of `streakKey`. A background sync that keeps failing retries
 * every 15 s (backing off to 5 min), and reporting every retry would bury the incident under copies of itself. The
 * streak ends, and the next failure reports again, at endClientErrorStreak( streakKey ): call it on the next success.
 *
 * @returns whether this call queued the error.
 */
export function queueClientErrorOncePerStreak( streakKey: string, err: unknown, ctx: object ): boolean {
    if ( reportedFailureStreaks.has( streakKey ) ) {
        return false;
    }
    reportedFailureStreaks.add( streakKey );
    clientStateManager.queueClientError( err, ctx );
    return true;
}

export function endClientErrorStreak( streakKey: string ): void {
    reportedFailureStreaks.delete( streakKey );
}
